import * as zod from "zod/v4";
//#region src/lazy-zod.ts
const NEVER = zod.NEVER;
const ZodIssueCode = zod.ZodIssueCode;
const DEFERRED_SCHEMA_METHODS = /* @__PURE__ */ new Set([
	"and",
	"array",
	"base64",
	"base64url",
	"catch",
	"catchall",
	"check",
	"cidrv4",
	"cidrv6",
	"clone",
	"cuid",
	"cuid2",
	"date",
	"datetime",
	"default",
	"duration",
	"e164",
	"email",
	"emoji",
	"endsWith",
	"exactOptional",
	"exclude",
	"extend",
	"extract",
	"gt",
	"gte",
	"guid",
	"includes",
	"input",
	"int",
	"ipv4",
	"ipv6",
	"jwt",
	"keyof",
	"ksuid",
	"length",
	"loose",
	"lowercase",
	"lt",
	"lte",
	"max",
	"merge",
	"mime",
	"min",
	"multipleOf",
	"nanoid",
	"negative",
	"nonempty",
	"nonnegative",
	"nonoptional",
	"nonpositive",
	"normalize",
	"nullable",
	"nullish",
	"omit",
	"optional",
	"or",
	"output",
	"overwrite",
	"partial",
	"passthrough",
	"pick",
	"pipe",
	"positive",
	"prefault",
	"readonly",
	"refine",
	"regex",
	"removeCatch",
	"removeDefault",
	"required",
	"rest",
	"safe",
	"safeExtend",
	"size",
	"slugify",
	"startsWith",
	"step",
	"strict",
	"strip",
	"superRefine",
	"time",
	"toLowerCase",
	"toUpperCase",
	"transform",
	"trim",
	"ulid",
	"unwrap",
	"uppercase",
	"url",
	"uuid",
	"uuidv4",
	"uuidv6",
	"uuidv7",
	"with",
	"xid"
]);
const SELF_RETURNING_METHODS = /* @__PURE__ */ new Set(["brand", "finite"]);
const UNHANDLED_PROPERTY = Symbol("unhandled lazy Zod property");
const globalRegistry = zod.globalRegistry;
function lazySchema(createSchema) {
	let schema;
	let proxy;
	const target = Object.create(null);
	let targetIsMaterialized = false;
	const resolveSchema = () => {
		schema ??= createSchema();
		return schema;
	};
	const readResolvedProperty = (property) => {
		const resolved = resolveSchema();
		const value = Reflect.get(resolved, property, resolved);
		return typeof value === "function" ? value.bind(resolved) : value;
	};
	const deferSchemaMethod = (property, args) => lazySchema(() => {
		const resolved = resolveSchema();
		const method = Reflect.get(resolved, property, resolved);
		if (typeof method !== "function") throw new TypeError(`Lazy Zod property ${String(property)} is not callable`);
		const result = Reflect.apply(method, resolved, args);
		if (!(result instanceof zod.ZodType)) throw new TypeError(`Lazy Zod method ${String(property)} did not return a schema`);
		return result;
	});
	const materializeTarget = () => {
		const resolved = resolveSchema();
		if (targetIsMaterialized) return resolved;
		Reflect.setPrototypeOf(target, Reflect.getPrototypeOf(resolved));
		for (const property of Reflect.ownKeys(resolved)) {
			const descriptor = Reflect.getOwnPropertyDescriptor(resolved, property);
			if (descriptor) Reflect.defineProperty(target, property, descriptor);
		}
		targetIsMaterialized = true;
		return resolved;
	};
	const readSpecialProperty = (property) => {
		switch (property) {
			case "description": {
				const metadata = globalRegistry.get(proxy);
				return metadata && typeof metadata === "object" ? Reflect.get(metadata, "description") : void 0;
			}
			case "apply": return (operation) => operation(proxy);
			case "register": return (registry, ...metadata) => {
				registry.add(proxy, ...metadata);
				return proxy;
			};
			case "meta": return (...metadata) => {
				if (metadata.length === 0) return globalRegistry.get(proxy);
				const registered = deferSchemaMethod(property, withoutRegistryIdentity(metadata));
				globalRegistry.add(registered, ...metadata);
				return registered;
			};
			case "describe": return (description) => {
				const described = deferSchemaMethod(property, [description]);
				globalRegistry.add(described, { description });
				return described;
			};
			case "toJSONSchema": return (...args) => Reflect.apply(zod.toJSONSchema, zod, [proxy, ...args]);
			default: return UNHANDLED_PROPERTY;
		}
	};
	proxy = new Proxy(target, {
		deleteProperty(_target, property) {
			const deletedFromTarget = Reflect.deleteProperty(target, property);
			const deletedFromSchema = Reflect.deleteProperty(resolveSchema(), property);
			return deletedFromTarget && deletedFromSchema;
		},
		get(_target, property, receiver) {
			const specialProperty = readSpecialProperty(property);
			if (specialProperty !== UNHANDLED_PROPERTY) return specialProperty;
			if (SELF_RETURNING_METHODS.has(property)) return () => proxy;
			if (DEFERRED_SCHEMA_METHODS.has(property)) return (...args) => deferSchemaMethod(property, args);
			return Reflect.getOwnPropertyDescriptor(target, property) ? Reflect.get(target, property, receiver) : readResolvedProperty(property);
		},
		getOwnPropertyDescriptor(_target, property) {
			const targetDescriptor = Reflect.getOwnPropertyDescriptor(target, property);
			if (targetDescriptor || targetIsMaterialized) return targetDescriptor;
			const descriptor = Reflect.getOwnPropertyDescriptor(resolveSchema(), property);
			return descriptor ? {
				...descriptor,
				configurable: true
			} : void 0;
		},
		getPrototypeOf() {
			return targetIsMaterialized ? Reflect.getPrototypeOf(target) : Reflect.getPrototypeOf(resolveSchema());
		},
		has(_target, property) {
			return Reflect.has(target, property) || Reflect.has(resolveSchema(), property);
		},
		ownKeys() {
			if (targetIsMaterialized) return Reflect.ownKeys(target);
			return [.../* @__PURE__ */ new Set([...Reflect.ownKeys(target), ...Reflect.ownKeys(resolveSchema())])];
		},
		preventExtensions() {
			const resolved = materializeTarget();
			return Reflect.preventExtensions(resolved) && Reflect.preventExtensions(target);
		},
		set(_target, property, value) {
			const resolved = resolveSchema();
			if (!targetIsMaterialized) return Reflect.set(resolved, property, value);
			return Reflect.set(resolved, property, value) && Reflect.set(target, property, value);
		}
	});
	return proxy;
}
function withoutRegistryIdentity(metadata) {
	const [firstMetadata] = metadata;
	if (!(firstMetadata && typeof firstMetadata === "object" && "id" in firstMetadata)) return metadata;
	return [Object.fromEntries(Object.entries(firstMetadata).filter(([key]) => key !== "id")), ...metadata.slice(1)];
}
function snapshotFactoryArgument(value) {
	return Array.isArray(value) ? [...value] : value;
}
function lazyFactory(factory) {
	const invoke = factory;
	return ((...args) => {
		const declaredArgs = args.map(snapshotFactoryArgument);
		return lazySchema(() => invoke(...declaredArgs));
	});
}
const array = lazyFactory(zod.array);
const boolean = lazyFactory(zod.boolean);
const discriminatedUnion = lazyFactory(zod.discriminatedUnion);
const email = lazyFactory(zod.email);
const enumFactory = lazyFactory(zod.enum);
const int = lazyFactory(zod.int);
const literal = lazyFactory(zod.literal);
const neverFactory = lazyFactory(zod.never);
const nullFactory = lazyFactory(zod.null);
const number = lazyFactory(zod.number);
const object = lazyFactory(zod.object);
const preprocess = lazyFactory(zod.preprocess);
const record = lazyFactory(zod.record);
const strictObject = lazyFactory(zod.strictObject);
const string = lazyFactory(zod.string);
const tuple = lazyFactory(zod.tuple);
const union = lazyFactory(zod.union);
const undefinedFactory = lazyFactory(zod.undefined);
const unknown = lazyFactory(zod.unknown);
const url = lazyFactory(zod.url);
const coerce = {
	...zod.coerce,
	bigint: lazyFactory(zod.coerce.bigint),
	boolean: lazyFactory(zod.coerce.boolean),
	date: lazyFactory(zod.coerce.date),
	number: lazyFactory(zod.coerce.number),
	string: lazyFactory(zod.coerce.string)
};
const iso = {
	...zod.iso,
	date: lazyFactory(zod.iso.date),
	datetime: lazyFactory(zod.iso.datetime),
	duration: lazyFactory(zod.iso.duration),
	time: lazyFactory(zod.iso.time)
};
//#endregion
export { NEVER, ZodIssueCode, array, boolean, coerce, discriminatedUnion, email, enumFactory as enum, int, iso, lazySchema, literal, neverFactory as never, nullFactory as null, number, object, preprocess, record, strictObject, string, tuple, undefinedFactory as undefined, union, unknown, url };

//# sourceMappingURL=lazy-zod.js.map